import { X, Eye } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { socket } from "../../../../components/mediasoup/socket";
import SecureStreamPlayer from "../../../../components/streaming/StreamPlayer";
import { recordingSocket } from "../../../../components/mediasoup/recordingSocket";
import {
  getAllFlags,
  getTimeLine,
} from "../../../../api/controllerDashboardApis";

import type { FlagRecord } from "../../../../api/controllerDashboardApis";

import LiveCourtroomsSidebar from "./LiveCourtRoomsidebar";
import { FocusTimeline } from "./FocusTimeline";
import { EditActionsBar } from "./EditActionsBar";
import { FocusFlagsPanel } from "./FocusFlagsPanel";
import type { Flag, Toast } from "../helpers/types";
import type { RoomStatus } from "../helpers/types";
import type {
  EditAction,
  FocusRoom,
  PositionedFlag,
  RoomTimelineData,
} from "../helpers/focusViewTypes";

import { calcNowMin, splitRoomFlags } from "../helpers/focusViewHelpers";

import { Toasts } from "../main-dahsboard/ControlStrip";

function toFlag(r: FlagRecord): Flag {
  const rawDuration = r.segmentDuration;
  const isValidDuration = rawDuration && !rawDuration.includes("NaN");
  return {
    id: r._id,
    tp: r.flagType.toLowerCase().includes("pii") ? "pii" : "conduct",
    rv: r.flagStatus === "dismissed",
    flagStatus: r.flagStatus ?? "unresolved",
    tm: r.timeStamp
      ? new Date(r.timeStamp).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : "—",
    rm: r.cnr ?? "—",
    tg: r.flagSubType,
    ds: r.description?.trim()
      ? r.description
      : `${r.flagType} · ${r.flagSubType}`,
    dr: isValidDuration ? rawDuration : "—",
    startTime: r.startTime,
    endTime: r.endTime,
  };
}

function withTimelinePosition(flags: Flag[], nowMin: number): PositionedFlag[] {
  const dayFrac = Math.max(nowMin, 1) / 480;
  return flags.map((f, i) => {
    const base = dayFrac * ((i + 1) / (flags.length + 1));
    return {
      ...f,
      tlPos: Math.min(dayFrac, base),
      deadline: f.rv ? undefined : Date.now() + 10 * 60 * 1000,
    };
  });
}

interface CourtroomPlayerModalProps {
  room: {
    id: string;
    hlsUrl?: string;
    status?: RoomStatus;
    cs?: string;
    jg?: string;
    blk?: string;
    vw?: number;
  };
  onClose: () => void;
}

export default function CourtroomPlayerModal({
  room,
  onClose,
}: CourtroomPlayerModalProps) {
  const [selectedRoom, setSelectedRoom] = useState<FocusRoom>({
    id: room.id,
    hlsUrl: room.hlsUrl,
    cs: room.cs ?? "",
    jg: room.jg ?? "",
    status: room.status ?? "live",
    blk: room.blk ?? "",
    bg: "#0b1624",
    gl: "#1a3565",
  });
  const [flags, setFlags] = useState<Flag[]>([]);
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());

  const [activeActions, setActiveActions] = useState<
    Record<EditAction, boolean>
  >({
    blur: false,
    mute: false,
    mask: false,
    cut: false,
  });

  const [tlData, setTlData] = useState<RoomTimelineData | null>(null);

  const [notes, setNotes] = useState("");

  const [toasts, setToasts] = useState<Toast[]>([]);

  const [toastCounter, setToastCounter] = useState(0);

  const roomId = room.id;

  const nowMin = useMemo(() => calcNowMin(), []);

  const addToast = useCallback(
    (msg: string, col = "#1A56DB") => {
      const id = toastCounter + 1;
      setToastCounter(id);
      setToasts((prev) => [...prev, { id, msg, col }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 2800);
    },
    [toastCounter],
  );

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  useEffect(() => {
    getAllFlags(1).then(({ data }) => {
      setFlags(data.map(toFlag));
    });
  }, []);

  useEffect(() => {
    const handleGetFlag = (response: any) => {
      const flagRecords: FlagRecord[] = Array.isArray(response)
        ? response
        : (response?.data ?? []);
      if (flagRecords.length === 0) return;
      const newFlag = toFlag(flagRecords[0]);
      setFlags((prev) => {
        if (prev.some((f) => f.id === newFlag.id)) return prev;
        return [newFlag, ...prev];
      });
    };

    recordingSocket.on("get-flag", handleGetFlag);
    return () => {
      recordingSocket.off("get-flag", handleGetFlag);
    };
  }, []);

  useEffect(() => {
    if (!selectedRoom?.id) return;

    let isCurrent = true;
    getTimeLine()
      .then((res: any) => {
        if (!isCurrent) return;
        const data = res?.data ?? res;
        const rows = data?.todayRecordingTimeline ?? [];
        const match = rows.find((r: any) => r?.courtRoomId === selectedRoom.id);
        if (!match) {
          setTlData(null);
          return;
        }

        const segs = (match.segments ?? []).map((seg: any) => {
          let type: RoomTimelineData["segs"][number]["t"] = "r";
          switch (seg?.type) {
            case "recorded+streaming":
              type = "l";
              break;
            case "flagged":
              type = "f";
              break;
            case "rec_only":
              type = "rc";
              break;
            default:
              type = "r";
          }
          return {
            t: type,
            f: Math.max((seg?.offsetEnd ?? 1) - (seg?.offsetStart ?? 0), 1),
            lb: seg?.type === "flagged" ? "⚑" : (seg?.label ?? ""),
            ti: seg?.startTime
              ? new Date(seg.startTime).toLocaleTimeString()
              : "",
            flagId: seg?.flagId,
          };
        });

        setTlData({ rm: match.courtRoomId ?? "", cs: match.cnr ?? "", segs });
      })
      .catch(() => setTlData(null));

    return () => {
      isCurrent = false;
    };
  }, [selectedRoom?.id]);

  const emit = (event: string, payload: object) => {
    socket.emit(event, { roomId, ...payload });
  };

  const handleSelectRoom = (r: FocusRoom) => {
    if (r.status === "offline") {
      addToast("Room offline", "#6B7280");
      return;
    }
    setSelectedRoom(r);
    setActiveActions({ blur: false, mute: false, mask: false, cut: false });
  };

  const toggleAction = (action: EditAction) => {
    setActiveActions((prev) => {
      const next = !prev[action];
      if (action === "blur") emit("controller-blur", { blurred: next });
      if (action === "mute") emit("controller-mute-audio", { muted: next });
      return { ...prev, [action]: next };
    });
  };

  const toggleResolve = (flagId: string) => {
    setResolvedIds((prev) => {
      const next = new Set(prev);
      if (next.has(flagId)) next.delete(flagId);
      else next.add(flagId);
      return next;
    });
  };

  const { active, missed, resolved } = useMemo(() => {
    if (!selectedRoom?.id) return { active: [], missed: [], resolved: [] };
    const positioned = withTimelinePosition(flags, nowMin);
    return splitRoomFlags(selectedRoom.id, positioned, nowMin);
  }, [flags, selectedRoom?.id, nowMin]);

  const allFlagsForRoomPositioned = useMemo(() => {
    const positioned = withTimelinePosition(flags, nowMin);
    return positioned.filter((f) => f.rm?.includes(selectedRoom?.id ?? ""));
  }, [flags, selectedRoom?.id, nowMin]);

  const isLiveRoom = selectedRoom?.status === "live";
  const allMissed = missed.length > 0 && active.length === 0;

  const handleSubmit = () => {
    const actsApplied = Object.entries(activeActions).filter(
      ([, v]) => v,
    ).length;
    let resolvedCount = 0;

    [...active, ...missed].forEach((f) => {
      if (resolvedIds.has(f.id)) resolvedCount++;
    });

    if (actsApplied === 0 && resolvedCount === 0) {
      addToast("Select an action or resolve a flag first", "#B45309");
      return;
    }

    if (resolvedCount > 0) {
      setFlags((prev) =>
        prev.map((f) =>
          resolvedIds.has(f.id)
            ? { ...f, rv: true, flagStatus: "dismissed" }
            : f,
        ),
      );
      setResolvedIds(new Set());
    }

    const msg =
      actsApplied > 0 && resolvedCount > 0
        ? `${actsApplied} action(s) applied · ${resolvedCount} flag(s) resolved`
        : actsApplied > 0
          ? `${actsApplied} action(s) applied to recording`
          : `${resolvedCount} flag(s) resolved`;

    addToast(msg, "#16A34A");
    setActiveActions({ blur: false, mute: false, mask: false, cut: false });
    setNotes("");
  };

  if (!room.hlsUrl) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
        <div className="text-gray-500 flex items-center gap-4">
          No stream URL provided for court {room.id}
          <button onClick={onClose} className="text-blue-600 text-sm">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex bg-white">
      <div className="flex-1 min-w-0 overflow-hidden bg-white flex flex-col shadow-[2px_0_12px_rgba(0,0,0,0.06)]">
        <div
          className="flex flex-col"
          style={{ flex: "0 0 62%", minHeight: 0 }}
        >
          <div className="shrink-0 flex items-start justify-between px-5 py-2 border-b border-gray-200 bg-white">
            <div className="flex flex-col gap-0">
              <div className="text-[11px] font-medium text-gray-500">
                {selectedRoom?.id} · {selectedRoom?.blk}
              </div>
              <div className="text-base font-semibold text-gray-900">
                {selectedRoom?.cs}
              </div>
              <div className="text-xs text-gray-500">{selectedRoom?.jg}</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex items-center gap-1.5 border border-gray-300 rounded-md px-2.5 py-1.5 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <X size={14} /> Close
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 bg-[#0b1624] flex items-center justify-center overflow-hidden relative">
            <div className="relative h-full w-full max-w-full overflow-hidden">
              <SecureStreamPlayer
                streamPath={selectedRoom?.hlsUrl ?? ""}
                user="Controller"
                ip="127.0.0.1"
                caseId={selectedRoom?.id ?? ""}
                mobile="9999999999"
                courtName={selectedRoom?.cs ?? ""}
                location="Court Room"
              />
              {(selectedRoom?.vw ?? 0) > 0 && (
                <div className="absolute bottom-2.5 left-3 text-white/80 text-[11px] flex items-center gap-1 pointer-events-none">
                  <Eye size={12} /> {selectedRoom.vw}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-5 py-3.5 border-b border-gray-200">
            <FocusTimeline
              tlData={tlData}
              roomFlags={[...active, ...missed]}
              allFlagsForRoom={allFlagsForRoomPositioned}
              nowMin={nowMin}
              isLiveRoom={isLiveRoom}
              onSelectionPreview={(label) => addToast(label, "#1A56DB")}
            />
          </div>

          <div className="px-5 py-3.5 border-b border-gray-200">
            <EditActionsBar
              activeActions={activeActions}
              onToggle={toggleAction}
              allMissed={allMissed}
            />
          </div>

          <div className="px-5 py-3.5">
            <FocusFlagsPanel
              active={active}
              missed={missed}
              resolved={resolved}
              cnrBlocks={tlData?.cnr}
              resolvedIds={resolvedIds}
              onToggleResolve={toggleResolve}
            />
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-2.5 px-5 py-3 bg-white border-t border-gray-200">
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)…"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-xs text-gray-900 bg-gray-50 outline-none focus:border-blue-600 focus:bg-white transition-colors"
          />

          <button
            type="button"
            onClick={handleSubmit}
            className="px-5 py-2 bg-blue-600 text-white border-none rounded-[7px] text-[13px] font-semibold hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            Submit
          </button>
        </div>
      </div>

      <LiveCourtroomsSidebar
        selectedRoomId={selectedRoom?.id}
        onSelectRoom={handleSelectRoom}
        flags={flags}
      />

      <Toasts toasts={toasts} />
    </div>
  );
}
